import React, { useEffect, useMemo, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { AddCircle, ArrowBack, Close, Visibility } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import {
  FINANCE_V2_HOSTNAME,
  INTRANETHOSTNAME,
  INVOICE_TRACKING_HOSTNAME,
} from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import {
  canViewPRs,
  isCreateAllowed,
  isStoreRole,
  isUpdateAllowed,
} from "../../../utils/common-utils";
import FileUpload from "../../../../common-utilities/core/file-uploader";
import PRItemRow from "../create-pr/add-item-modal";
import SingleStorePRSection from "../create-pr/single-store-row";
import BudgetAvailability from "../create-pr/budget-availability";
import ResolvedStoresViewer from "../create-pr/resolved-stores-viewer";
import {
  PR_ITEM_MODEL,
  getPRTotalAmount,
  getLineItemTotal,
  getBudgetTypeLabel,
  getPooledQuantity,
  mapPrFileUploadResponse,
  normalizeCode,
  buildLocationFirstStores,
} from "../create-pr/helper";
import { mapPRResponseToFormState, buildEditPayload } from "./helper";
import DownloadTemplateButton from "../../master-data/common-components/upload-template";
import { uploadTemplates } from "../../master-data/constants";
import "./index.scss";

const renderHOColumnHeader = () => (
  <div className="pr-item-row pr-item-row-header">
    <span>Subcategory</span>
    <span>Item</span>
    <span>Description</span>
    <span>HSN</span>
    <span>UOM</span>
    <span>Qty</span>
    <span>Rate (₹)</span>
    <span>Tax %</span>
    <span>Amount</span>
    <span>Tax Amt</span>
    <span>Total</span>
    <span>Vendor</span>
    <span>Req. By Date</span>
    <span>Budget Head</span>
    <span>Remarks</span>
    <span></span>
  </div>
);

const EditPR = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // The PR's own status as last loaded from the server -- drives the persistent
  // "this will restart approval" notice below. Not touched by local form edits.
  const [loadedPrStatus, setLoadedPrStatus] = useState("");
  const isLiveApprovalEdit = ["approval_in_progress", "approved"].includes(loadedPrStatus);

  const [prType, setPRType] = useState("");
  const [prScope, setPRScope] = useState("SINGLE_STORE");
  const isHeadOffice = prScope === "HEAD_OFFICE";

  // The HO site actually saved on the PR -- distinct from `budgetDetails.site`, which for
  // HEAD_OFFICE PRs is just one arbitrary pooled STORE whose budget was scraped for the
  // category line-item list.
  const [hoContextSiteId, setHoContextSiteId] = useState("");

  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [prItemsByLineItem, setPRItemsByLineItem] = useState({});
  const [budgetDetails, setBudgetDetails] = useState({});
  const [activeBudgetId, setActiveBudgetId] = useState(null);

  const [formDetails, setFormDetails] = useState({});
  const [headerRequiredByDate, setHeaderRequiredByDate] = useState("");

  const [storeList, setStoreList] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [procurementDestination, setProcurementDestination] = useState("SELF");

  // Concrete store(s) each HO multi-store item targets -- already the resolved list for
  // every destination type. Empty for SELF (single site: the HO itself) and for
  // SINGLE_STORE, neither of which need a per-store quantity split.
  const targetStores = isHeadOffice && procurementDestination !== "SELF" ? selectedStores : [];

  // Store Master is only used to backfill city/state/region on Location Master records
  // that are missing it (matched by store code). SELF/SPECIFIC_STORES stay on storeList
  // (the Location master) untouched.
  const [storeMasterList, setStoreMasterList] = useState([]);

  // Resolved-stores viewer (read-only) for the City/State/Region chips -- purely a display
  // aid, never read by buildEditPayload/validate/mapPrFileUploadResponse.
  const [viewerAnchor, setViewerAnchor] = useState(null);
  const [viewerContext, setViewerContext] = useState(null);

  const resolvedHOStores = useMemo(
    () => buildLocationFirstStores(storeList, storeMasterList),
    [storeList, storeMasterList]
  );
  const cityOptions = useMemo(
    () => [...new Set(resolvedHOStores.map((s) => s.city).filter(Boolean))].sort(),
    [resolvedHOStores]
  );
  const stateOptions = useMemo(
    () => [...new Set(resolvedHOStores.map((s) => s.state).filter(Boolean))].sort(),
    [resolvedHOStores]
  );
  const regionOptions = useMemo(
    () => [...new Set(resolvedHOStores.map((s) => s.region).filter(Boolean))].sort(),
    [resolvedHOStores]
  );

  const getResolvedStores = (field, values) => {
    const valueSet = new Set(values.map(normalizeCode));
    return resolvedHOStores.filter((s) => valueSet.has(normalizeCode(s[field])));
  };

  const [subcategoryMap, setSubcategoryMap] = useState({});
  const [lineItemBudgetStats, setLineItemBudgetStats] = useState({});

  const [departmentList, setDepartmentList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [prConfig, setPRConfig] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prUploadErrors, setPRUploadErrors] = useState([]);

  // The PR's own persisted site identity -- always sourced from the saved PR response
  // itself (see mapPRResponseToFormState), never re-derived from budgetDetails (a pooled
  // STORE for HEAD_OFFICE) or a storeList lookup that may still be loading.
  const [siteName, setSiteName] = useState("");
  const [siteCode, setSiteCode] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [financialYear, setFinancialYear] = useState("");

  useEffect(() => {
    if (!canViewPRs() || !(isCreateAllowed("pr") || isUpdateAllowed("pr") || isStoreRole())) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }
    loadDepartments();
    loadVendors();
    loadExistingPR();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedLineItems.length === 0) return;

    selectedLineItems.forEach((lineItem) => {
      const catId = Number(lineItem.category?.id);
      if (!catId) {
        return;
      }
      loadSubcategoriesForLineItem(lineItem.id, catId);
      loadBudgetStatsForLineItem(lineItem);
    });
  }, [selectedLineItems, storeList]); // eslint-disable-line react-hooks/exhaustive-deps

  // The saved PR's target stores (mapped.selectedStores) only carry bare ids/placeholder
  // name+code until the backing store lists finish loading -- hydrate them with the real
  // record (name/code, plus city/state/region for CITY/STATE/REGION) the same way the
  // create-PR screen's own Autocomplete onChange handlers populate selectedStores, so the
  // target store chips and each item's per-store quantity boxes show the real store instead
  // of a blank label.
  useEffect(() => {
    if (!isHeadOffice || procurementDestination === "SELF") return;

    if (procurementDestination === "SPECIFIC_STORES") {
      if (storeList.length === 0) return;
      setSelectedStores((prev) =>
        prev.map(
          (s) => storeList.find((st) => String(st._id ?? st.id) === String(s._id ?? s.id)) || s
        )
      );
      return;
    }

    if (resolvedHOStores.length === 0) return;
    if (procurementDestination === "CITY" && selectedCities.length > 0) {
      setSelectedStores(getResolvedStores("city", selectedCities));
    } else if (procurementDestination === "STATE" && selectedStates.length > 0) {
      setSelectedStores(getResolvedStores("state", selectedStates));
    } else if (procurementDestination === "REGION" && selectedRegions.length > 0) {
      setSelectedStores(getResolvedStores("region", selectedRegions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeList, resolvedHOStores]);

  // ── data loaders ─────────────────────────────────────────────────────────────

  const loadExistingPR = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}/${editId}`
    )
      .then((pr) => {
        if (!pr || pr.message) {
          if (pr?.message) toast.error(pr.message);
          setNotFound(true);
          setIsLoading(false);
          return;
        }

        const mapped = mapPRResponseToFormState(pr);
        setLoadedPrStatus(mapped.loadedPrStatus);
        setPRType(mapped.prType);
        setPRScope(mapped.prScope);
        setSiteName(mapped.siteName);
        setSiteCode(mapped.siteCode);
        setCreatorName(mapped.creatorName);
        setFinancialYear(mapped.financialYear);
        setHeaderRequiredByDate(mapped.headerRequiredByDate);
        setFormDetails(mapped.formDetails);
        setAttachments(mapped.attachments);

        if (mapped.isHeadOffice) {
          setProcurementDestination(mapped.procurementDestination);
          setHoContextSiteId(mapped.hoContextSiteId);
          // storeList must load regardless of destination (including SELF) so the
          // real HO site's name/code can be resolved for the header.
          loadStores();
          if (mapped.procurementDestination !== "SELF") {
            loadStoreMasterGeoOptions();
          }
          if (mapped.selectedStores.length) setSelectedStores(mapped.selectedStores);
          if (mapped.procurementDestination === "CITY") {
            setSelectedCities(mapped.selectedCities);
          } else if (mapped.procurementDestination === "STATE") {
            setSelectedStates(mapped.selectedStates);
          } else if (mapped.procurementDestination === "REGION") {
            setSelectedRegions(mapped.selectedRegions);
          }
        }

        setPRItemsByLineItem(mapped.prItemsByLineItem);

        if (mapped.budgetId) {
          setActiveBudgetId(mapped.budgetId);
          loadBudgetForEdit(mapped.budgetId, mapped.lineItemIds);
        }
        if (mapped.departmentId) loadPRConfig(mapped.departmentId);

        setIsLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setIsLoading(false);
      });
  };

  const loadBudgetForEdit = (bId, lineItemIds) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGETS}`,
      null,
      { page: 1, limit: 10, _id: bId, populateLocation: true }
    )
      .then((response) => {
        if (!response.message && response.results?.length > 0) {
          const budget = response.results[0];
          const site = budget.site || {};
          setBudgetDetails({
            ...budget,
            siteName: site.name || budget.siteName || "",
            siteCode: site.code || budget.siteCode || "",
            site: site._id || site.id || budget.site,
          });
          const filtered = (budget.lineItems || []).filter((l) => lineItemIds.includes(l.id));
          setSelectedLineItems(filtered);
        }
      })
      .catch(console.log);
  };

  const loadStores = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_LOCATION_LIST}`,
      null,
      { additionalFields: "locationType,city,region,state" }
    )
      .then((res) => {
        if (!res.message) setStoreList(Array.isArray(res) ? res : res?.results || []);
      })
      .catch(console.log);
  };

  // Read-only fetch of the Store Master -- used only to backfill city/state/region on
  // Location Master records that are missing it (see resolvedHOStores/buildLocationFirstStores).
  const loadStoreMasterGeoOptions = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${FINANCE_REST_URLS.GET_ALL_STORES}`,
      null,
      { page: 1, limit: 5000 }
    )
      .then((res) => {
        if (res.message) return;
        setStoreMasterList(res.results || []);
      })
      .catch(console.log);
  };

  const loadSubcategoriesForLineItem = (lineItemId, categoryId) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.SUBCATEGORY}`,
      null,
      { page: 1, limit: 1000, category: categoryId }
    )
      .then((res) => {
        if (!res.message) {
          setSubcategoryMap((prev) => ({
            ...prev,
            [lineItemId]: res.results || [],
          }));
        }
      })
      .catch(console.log);
  };

  // HEAD_OFFICE PRs: this line item already belongs to budgetDetails, the single
  // HO-scoped budget resolved from the saved PR's own site (hoContextSiteId) — look
  // it up directly, the same way the SINGLE_STORE branch below does, instead of
  // re-pooling across every STORE location.
  const loadHeadOfficeBudgetStatsForLineItem = (lineItem) => {
    const lineItemId = lineItem._id ?? lineItem.id;
    const budgetId = budgetDetails._id ?? budgetDetails.id;
    if (!lineItemId || !budgetId) return;

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGET_AVAILABILITY}`,
      null,
      { lineItemIds: lineItemId, budgetIds: budgetId }
    )
      .then((res) => {
        if (!res.message) {
          setLineItemBudgetStats((prev) => ({ ...prev, [lineItem.id]: res }));
        }
      })
      .catch(console.log);
  };

  const loadBudgetStatsForLineItem = (lineItem) => {
    const budgetHead = lineItem._id || lineItem.id;
    const financialYear = budgetDetails.financialYear;
    if (!financialYear || !budgetHead) return;

    if (isHeadOffice) {
      loadHeadOfficeBudgetStatsForLineItem(lineItem);
      return;
    }

    const storeId = budgetDetails.site;
    if (!storeId) return;

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGET_AVAILABILITY}`,
      null,
      { budgetHead, storeId, financialYear }
    )
      .then((res) => {
        if (!res.message) {
          setLineItemBudgetStats((prev) => ({ ...prev, [lineItem.id]: res }));
        }
      })
      .catch(console.log);
  };

  const loadDepartments = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${FINANCE_REST_URLS.DEPARTMENTS}`,
      null,
      { page: 1, limit: 1000 }
    )
      .then((res) => {
        if (!res.message) {
          setDepartmentList(Array.isArray(res) ? res : res?.results || []);
        }
      })
      .catch(console.log);
  };

  const loadVendors = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INVOICE_TRACKING_HOSTNAME}${FINANCE_REST_URLS.VENDOR_LIST}`,
      null,
      { page: 1, limit: 1000, additionalFields: "paymentTerms,gst,address" }
    )
      .then((response) => {
        if (!response.message) {
          const list = response || [];
          setVendors(list);
          setFormDetails((prev) => {
            if (prev.preferredVendorCode && !prev.preferredVendor) {
              const found = list.find((v) => v.code === prev.preferredVendorCode);
              if (found) {
                return {
                  ...prev,
                  preferredVendor: found,
                  paymentTerms: prev.paymentTerms || found.paymentTerms || "",
                };
              }
            }
            return prev;
          });
        }
      })
      .catch(console.log);
  };

  const loadPRConfig = (departmentId) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_CONFIG}`,
      null,
      { department: departmentId }
    )
      .then((res) => {
        if (!res.message) setPRConfig(res || {});
      })
      .catch(console.log);
  };

  // ── item row handlers ─────────────────────────────────────────────────────────

  const onRowAdd = (lineItem) => {
    const items = prItemsByLineItem[lineItem.id] || [];
    setPRItemsByLineItem({
      ...prItemsByLineItem,
      [lineItem.id]: [
        ...items,
        {
          ...PR_ITEM_MODEL,
          budgetHead: lineItem.name || "",
          requiredByDate: headerRequiredByDate,
          _categoryId: Number(lineItem.category?.id),
          _tempId: `new-${Date.now()}`,
        },
      ],
    });
  };

  const updateItem = (lineItemId, index, updatedItem) => {
    const items = [...(prItemsByLineItem[lineItemId] || [])];
    items[index] = updatedItem;
    setPRItemsByLineItem({ ...prItemsByLineItem, [lineItemId]: items });
  };

  const deleteItem = (lineItemId, index) => {
    const items = [...(prItemsByLineItem[lineItemId] || [])];
    items.splice(index, 1);
    setPRItemsByLineItem({ ...prItemsByLineItem, [lineItemId]: items });
  };

  // Excel upload: merges resolved rows into their matching budget-head groups alongside
  // whatever's already there. Invalid rows are never merged in; they're only surfaced in
  // prUploadErrors.
  const onPrItemsUpload = async (response) => {
    const { data } = response;
    const { grouped, errors } = await mapPrFileUploadResponse(data, {
      selectedLineItems,
      storeList,
      storeMasterList,
      procurementDestination,
      selectedStores,
      prType,
      financialYear: budgetDetails.financialYear,
      hoSiteId: hoContextSiteId || budgetDetails.site,
      prDate: formDetails.prDate,
      isHeadOffice,
    });
    if (Object.keys(grouped).length > 0) {
      setPRItemsByLineItem((prev) => {
        const next = { ...prev };
        Object.entries(grouped).forEach(([lineItemId, items]) => {
          next[lineItemId] = [...(next[lineItemId] || []), ...items];
        });
        return next;
      });
    }
    setPRUploadErrors(errors);
    if (errors.length > 0) {
      toast.warning(
        `${errors.length} row(s) could not be added — see details below the upload button.`,
        { autoClose: 4000 }
      );
    }
  };

  // ── submit ────────────────────────────────────────────────────────────────────

  const validate = (shouldSubmit) => {
    const allItems = Object.values(prItemsByLineItem).flat();
    const submitItems = isHeadOffice
      ? allItems
      : allItems.filter((i) => (parseFloat(i.quantity) || 0) > 0);

    if (submitItems.length === 0) {
      toast.error("Please add at least one item with quantity > 0");
      return false;
    }
    if (shouldSubmit && isHeadOffice && allItems.some((i) => getPooledQuantity(i) <= 0)) {
      toast.error("Every item must have a quantity greater than 0 to submit this PR");
      return false;
    }
    if (isHeadOffice && procurementDestination === "SPECIFIC_STORES" && selectedStores.length === 0) {
      toast.error("Please select at least one target store");
      return false;
    }
    if (isHeadOffice && procurementDestination === "CITY" && selectedCities.length === 0) {
      toast.error("Please select at least one target city");
      return false;
    }
    if (isHeadOffice && procurementDestination === "STATE") {
      if (selectedStates.length === 0) {
        toast.error("Please select a target state");
        return false;
      }
      if (selectedStates.length > 1) {
        toast.error("Only one target state can be selected");
        return false;
      }
    }
    if (isHeadOffice && procurementDestination === "REGION") {
      if (selectedRegions.length === 0) {
        toast.error("Please select a target region");
        return false;
      }
      if (selectedRegions.length > 1) {
        toast.error("Only one target region can be selected");
        return false;
      }
    }
    if (prConfig.requireJustification && !formDetails.justification?.trim()) {
      toast.error("Justification is required for this department");
      return false;
    }
    if (prConfig.requireAttachment && attachments.length === 0) {
      toast.error("Supporting document is required for this department");
      return false;
    }
    return true;
  };

  const submitPR = (prId) => {
    invokeApi(HTTP_METHODS.POST, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SUBMIT}${Number(prId)}`)
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("PR submitted for approval successfully");
          navigate(`/finance-v2/purchase-requisition/${prId}`);
        }
      })
      .catch(() => toast.error("Failed to submit PR"))
      .finally(() => setIsSubmitting(false));
  };

  const savePR = (shouldSubmit) => {
    if (!validate(shouldSubmit)) return;
    setIsSubmitting(true);
    const payload = buildEditPayload({
      prItemsByLineItem,
      isHeadOffice,
      formDetails,
      attachments,
      hoContextSiteId,
      headerRequiredByDate,
    });

    invokeApi(
      HTTP_METHODS.PUT,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}/${editId}`,
      payload
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
          setIsSubmitting(false);
          return;
        }
        toast.success("PR updated successfully");
        const SUBMITTABLE_STATUSES = ["created", "reconsider", "draft", "Draft"];
        if (shouldSubmit && SUBMITTABLE_STATUSES.includes(res.status)) {
          submitPR(res.id || res._id || editId);
        } else if (shouldSubmit) {
          toast.success(
            "This edit didn't require restarting approval, so the PR's current approval cycle continues unchanged."
          );
          navigate(`/finance-v2/purchase-requisition/${res.id || res._id || editId}`);
          setIsSubmitting(false);
        } else {
          navigate(`/finance-v2/purchase-requisition/${res.id || res._id || editId}`);
          setIsSubmitting(false);
        }
      })
      .catch(() => {
        toast.error("Failed to save PR");
        setIsSubmitting(false);
      });
  };

  // ── render ────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="finance-v2-create-po-container">
        <div className="po-create-header">
          <div className="left-container">
            <div className="back-title">
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBack />
              </IconButton>
              <Typography className="title">Edit Purchase Requisition</Typography>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 16 }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading PR details...</Typography>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="finance-v2-create-po-container">
        <Typography color="error" sx={{ p: 2 }}>
          PR not found.
        </Typography>
        <Button onClick={() => navigate(-1)} sx={{ ml: 2 }}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="finance-v2-create-po-container">
      {/* Page header */}
      <div className="po-create-header">
        <div className="left-container">
          <div className="back-title">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography className="title">Edit Purchase Requisition</Typography>
          </div>
          <div className="info-section">
            <div className="info-box">
              <span>Site Name</span>
              <span>{siteName || "-"}</span>
            </div>
            <div className="info-box">
              <span>Site Code</span>
              <span>{siteCode || "-"}</span>
            </div>
            <div className="info-box">
              <span>PR Type</span>
              <span>{prType || "-"}</span>
            </div>
            <div className="info-box">
              <span>PR Amount</span>
              <span>₹{getPRTotalAmount(prItemsByLineItem)}</span>
            </div>
          </div>
        </div>
        <div className="right-container">
          <Button variant="outlined" disabled={isSubmitting} onClick={() => savePR(false)}>
            Save as Draft
          </Button>
          <Button variant="outlined" disabled={isSubmitting} onClick={() => savePR(true)}>
            Save &amp; Submit
          </Button>
        </div>
      </div>

      {isLiveApprovalEdit && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This Purchase Requisition currently has {loadedPrStatus === "approved" ? "been approved" : "an approval in progress"}.
          Saving changes here will stop that cycle — resubmission will restart approval from stage one.
          Earlier approval history is kept and will remain visible.
        </Alert>
      )}

      {/* Form fields */}
      <div className="form-container">
        {/* Row 1: Department | PR Scope | Requested By | Financial Year | Budget Type */}
        <div className="form-row">
          {isStoreRole() ? (
            <TextField size="small" label="Department" value="Store" disabled />
          ) : (
            <Autocomplete
              value={formDetails.department || null}
              disabled
              onChange={(e, val) => {
                setFormDetails((prev) => ({ ...prev, department: val }));
                if (val?._id || val?.id) loadPRConfig(val._id || val.id);
              }}
              options={departmentList}
              getOptionLabel={(op) => op.name || ""}
              isOptionEqualToValue={(opt, val) => (opt._id || opt.id) === (val._id || val.id)}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Department" />
              )}
            />
          )}
          <TextField
            value={isHeadOffice ? "Head Office" : "Single Store"}
            disabled
            size="small"
            label="PR Scope"
            fullWidth
          />
          <TextField
            value={creatorName}
            disabled
            size="small"
            label="Requested By"
            fullWidth
          />
          <TextField
            value={financialYear}
            disabled
            size="small"
            label="Financial Year"
            fullWidth
          />
          <TextField
            value={getBudgetTypeLabel(budgetDetails.budgetType)}
            disabled
            size="small"
            label="Budget Type"
            fullWidth
          />
        </div>

        {/* Row 2: PR Date | Required By Date | Remarks | Justification | Payment Terms */}
        <div className="form-row">
          <TextField
            label="PR Date"
            type="date"
            value={formDetails.prDate || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, prDate: e.target.value }))
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Required By Date"
            type="date"
            value={headerRequiredByDate}
            onChange={(e) => setHeaderRequiredByDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Remarks"
            value={formDetails.remarks || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, remarks: e.target.value }))
            }
            size="small"
            fullWidth
          />
          <TextField
            label={`Justification${prConfig.requireJustification ? " *" : ""}`}
            value={formDetails.justification || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, justification: e.target.value }))
            }
            size="small"
            fullWidth
          />
          <TextField
            value={formDetails.paymentTerms || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, paymentTerms: e.target.value }))
            }
            size="small"
            label="Payment Terms"
            fullWidth
          />
        </div>

        {/* HEAD_OFFICE: procurement destination + conditional target picker */}
        {isHeadOffice && (
          <div style={{ marginTop: 16 }}>
            <FormControl>
              <FormLabel>Procurement destination</FormLabel>
              <RadioGroup
                row
                value={procurementDestination}
                onChange={(e) => {
                  const dest = e.target.value;
                  setProcurementDestination(dest);
                  setSelectedStores([]);
                  setSelectedCities([]);
                  setSelectedStates([]);
                  setSelectedRegions([]);
                  if (dest !== "SELF" && storeList.length === 0) {
                    loadStores();
                  }
                  if (dest !== "SELF" && cityOptions.length === 0) {
                    loadStoreMasterGeoOptions();
                  }
                }}
              >
                <FormControlLabel value="SELF" control={<Radio />} label="Head Office" />
                <FormControlLabel value="SPECIFIC_STORES" control={<Radio />} label="Stores" />
                <FormControlLabel value="CITY" control={<Radio />} label="Cities" />
                <FormControlLabel value="STATE" control={<Radio />} label="States" />
                <FormControlLabel value="REGION" control={<Radio />} label="Regions" />
              </RadioGroup>
            </FormControl>
            {procurementDestination === "SPECIFIC_STORES" && (
              <Autocomplete
                multiple
                value={selectedStores}
                onChange={(e, val) => setSelectedStores(val || [])}
                options={storeList.filter(
                  (s) =>
                    s.locationType === "STORE" &&
                    String(s._id || s.id) !== String(hoContextSiteId)
                )}
                getOptionLabel={(op) => (op.code ? `${op.code} - ${op.name}` : op.name || "")}
                isOptionEqualToValue={(opt, val) =>
                  (opt._id || opt.id) === (val._id || val.id)
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Target Stores *" />
                )}
                style={{ marginTop: 12 }}
              />
            )}
            {procurementDestination === "CITY" && (
              <>
                <Autocomplete
                  multiple
                  value={selectedCities}
                  onChange={(e, val) => {
                    const cities = val || [];
                    setSelectedCities(cities);
                    setSelectedStores(getResolvedStores("city", cities));
                  }}
                  options={cityOptions}
                  renderTags={(value, getTagProps) =>
                    value.map((val, index) => {
                      const matches = getResolvedStores("city", [val]);
                      return (
                        <Chip
                          icon={<Visibility fontSize="small" />}
                          label={`${val} · ${matches.length} store${matches.length === 1 ? "" : "s"}`}
                          size="small"
                          {...getTagProps({ index })}
                          onClick={(e) => {
                            setViewerAnchor(e.currentTarget);
                            setViewerContext({ title: `Stores in ${val}`, stores: matches });
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Target Cities *" />
                  )}
                  style={{ marginTop: 12 }}
                />
                {selectedCities.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Click a selected value to view resolved stores.
                  </Typography>
                )}
              </>
            )}
            {procurementDestination === "STATE" && (
              <>
                <Autocomplete
                  value={selectedStates[0] || null}
                  onChange={(e, val) => {
                    // Single-select: STATE targeting must resolve to exactly one state group.
                    const states = val ? [val] : [];
                    setSelectedStates(states);
                    setSelectedStores(getResolvedStores("state", states));
                  }}
                  options={stateOptions}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Target State *" />
                  )}
                  style={{ marginTop: 12 }}
                />
                {selectedStates.map((val) => {
                  const matches = getResolvedStores("state", [val]);
                  return (
                    <Chip
                      key={val}
                      icon={<Visibility fontSize="small" />}
                      label={`${val} · ${matches.length} store${matches.length === 1 ? "" : "s"}`}
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={(e) => {
                        setViewerAnchor(e.currentTarget);
                        setViewerContext({ title: `Stores in ${val}`, stores: matches });
                      }}
                    />
                  );
                })}
                {selectedStates.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Click the selected value to view resolved stores.
                  </Typography>
                )}
              </>
            )}
            {procurementDestination === "REGION" && (
              <>
                <Autocomplete
                  value={selectedRegions[0] || null}
                  onChange={(e, val) => {
                    // Single-select: REGION targeting must resolve to exactly one region group.
                    const regions = val ? [val] : [];
                    setSelectedRegions(regions);
                    setSelectedStores(getResolvedStores("region", regions));
                  }}
                  options={regionOptions}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Target Region *" />
                  )}
                  style={{ marginTop: 12 }}
                />
                {selectedRegions.map((val) => {
                  const matches = getResolvedStores("region", [val]);
                  return (
                    <Chip
                      key={val}
                      icon={<Visibility fontSize="small" />}
                      label={`${val} · ${matches.length} store${matches.length === 1 ? "" : "s"}`}
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={(e) => {
                        setViewerAnchor(e.currentTarget);
                        setViewerContext({ title: `Stores in ${val}`, stores: matches });
                      }}
                    />
                  );
                })}
                {selectedRegions.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Click the selected value to view resolved stores.
                  </Typography>
                )}
              </>
            )}
            <ResolvedStoresViewer
              anchorEl={viewerAnchor}
              open={Boolean(viewerAnchor)}
              onClose={() => setViewerAnchor(null)}
              title={viewerContext?.title}
              stores={viewerContext?.stores || []}
            />
          </div>
        )}
      </div>

      {/* Attachments */}
      <div className="po-additional-attachment-container">
        <FileUpload
          uploadText={`Supporting Documents${prConfig.requireAttachment ? " *" : ""}`}
          clsName="add-additional-doc"
          id="pr-attachments"
          url={`${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.UPLOAD_DOCUMENT}`}
          callBack={(response) => {
            const url = response?.data?.docUrl || response?.data?.url;
            if (url) setAttachments((prev) => [...prev, url]);
          }}
          formDataKey="file"
        />
        {attachments.map((url, idx) => (
          <Chip
            key={idx}
            label={`Doc ${idx + 1}`}
            onDelete={() => setAttachments(attachments.filter((_, i) => i !== idx))}
            onClick={() => window.open(url, "_blank")}
          />
        ))}
      </div>

      {/* Excel upload — HEAD_OFFICE only */}
      {isHeadOffice && (
        <div className="po-attachment-form-container">
          <FileUpload
            uploadText="Upload PR Item Details"
            clsName="add-additional-doc"
            id="pr-items-excel"
            url={`${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.UPLOAD_MASTER_DATA}pr-item-details?site=${
              hoContextSiteId || ""
            }&lineItemIds=${selectedLineItems.map((li) => li.id).join(",")}`}
            callBack={onPrItemsUpload}
            formDataKey="file"
          />
          <DownloadTemplateButton
            type={
              procurementDestination === "SELF"
                ? uploadTemplates["pr-items-self"]
                : uploadTemplates["pr-items"]
            }
          />
        </div>
      )}
      {isHeadOffice && prUploadErrors.length > 0 && (
        <div className="po-attachment-form-container finance-v2-pr-upload-errors">
          <div className="pr-upload-errors-header">
            <Typography color="error" variant="subtitle2">
              {prUploadErrors.length} row(s) skipped due to validation errors
            </Typography>
            <IconButton size="small" onClick={() => setPRUploadErrors([])}>
              <Close fontSize="small" />
            </IconButton>
          </div>
          {prUploadErrors.map((err, i) => (
            <Typography key={i} color="error" variant="body2">
              Row {err.row ?? "-"}: {err.message}
            </Typography>
          ))}
        </div>
      )}

      {/* Items per budget line item */}
      <div className="finance-v2-po-add-items-container">
        <div className="added-po-items-list">
          {selectedLineItems.map((lineItem) => {
            const items = prItemsByLineItem[lineItem.id] || [];
            const lineItemTotal = parseFloat(getLineItemTotal(items));
            const available = lineItem.availableAmount || 0;
            const isOver = lineItemTotal > available;
            const stats = lineItemBudgetStats[lineItem.id];

            return (
              <Accordion key={lineItem.id} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="po-accordion-summary">
                  <div className="accordion-header">
                    <div className="left-container">
                      <Typography>
                        {lineItem.name} ({items.length})
                      </Typography>
                      {isHeadOffice && (
                        <IconButton
                          onClick={(e) => {
                            onRowAdd(lineItem);
                            e.stopPropagation();
                          }}
                        >
                          <AddCircle color="primary" />
                        </IconButton>
                      )}
                    </div>
                    <div className="right-container">
                      <BudgetAvailability
                        stats={stats}
                        available={available}
                        lineItemTotal={lineItemTotal}
                        isOver={isOver}
                      />
                    </div>
                  </div>
                </AccordionSummary>

                <AccordionDetails sx={{ overflowX: "auto", p: 1 }}>
                  {isHeadOffice ? (
                    <>
                      {items.length > 0 && renderHOColumnHeader()}
                      {items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          Click + to add items
                        </Typography>
                      ) : (
                        items.map((prItem, index) => (
                          <PRItemRow
                            key={prItem._tempId || index}
                            prItem={prItem}
                            subcategoryList={subcategoryMap[lineItem.id] || []}
                            allVendors={vendors}
                            budgetHeadName={lineItem.name || ""}
                            headerRequiredByDate={headerRequiredByDate}
                            prDate={formDetails.prDate}
                            targetStores={targetStores}
                            onUpdate={(item) => updateItem(lineItem.id, index, item)}
                            onDelete={() => deleteItem(lineItem.id, index)}
                          />
                        ))
                      )}
                    </>
                  ) : (
                    <SingleStorePRSection
                      lineItem={lineItem}
                      existingItems={prItemsByLineItem[lineItem.id] || []}
                      subcategoryList={subcategoryMap[lineItem.id] || []}
                      prDate={formDetails.prDate}
                      onItemsChange={(rows) =>
                        setPRItemsByLineItem((prev) => ({ ...prev, [lineItem.id]: rows }))
                      }
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}

          {/* Items whose lineItem is no longer among selectedLineItems (e.g. "misc", or a
              budget line item that's since been removed) -- still editable so nothing on
              the saved PR silently disappears from the form. */}
          {Object.entries(prItemsByLineItem)
            .filter(
              ([key]) => key === "misc" || !selectedLineItems.find((l) => l.id === key)
            )
            .map(([lineItemId, items]) => (
              <Accordion key={lineItemId} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="po-accordion-summary">
                  <div className="accordion-header">
                    <div className="left-container">
                      <Typography>Other Items ({items.length})</Typography>
                      <IconButton
                        onClick={(e) => {
                          setPRItemsByLineItem((prev) => ({
                            ...prev,
                            [lineItemId]: [
                              ...(prev[lineItemId] || []),
                              {
                                ...PR_ITEM_MODEL,
                                requiredByDate: headerRequiredByDate,
                                _tempId: `new-${Date.now()}`,
                              },
                            ],
                          }));
                          e.stopPropagation();
                        }}
                      >
                        <AddCircle color="primary" />
                      </IconButton>
                    </div>
                  </div>
                </AccordionSummary>
                <AccordionDetails sx={{ overflowX: "auto", p: 1 }}>
                  {items.map((prItem, index) => (
                    <PRItemRow
                      key={prItem._tempId || index}
                      prItem={prItem}
                      subcategoryList={subcategoryMap[lineItemId] || []}
                      allVendors={vendors}
                      budgetHeadName=""
                      headerRequiredByDate={headerRequiredByDate}
                      prDate={formDetails.prDate}
                      targetStores={targetStores}
                      onUpdate={(item) => updateItem(lineItemId, index, item)}
                      onDelete={() => deleteItem(lineItemId, index)}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
        </div>
      </div>
    </div>
  );
};

export default EditPR;
